import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Dropdown } from 'antd';
import Icon, { LeftOutlined, EyeOutlined } from '@ant-design/icons';
import { EditIcon, TriangleDownIcon, TriangleUpIcon } from 'components/Elements/CustomSVGIcon';
import Main from 'components/layout/Main';
import { RequestFormNavigationState } from 'components/layout/RequestFormLayout/types';
import { CombinedStates, AppDispatch } from 'core/types';
import { ADMIN_ACCESS_LEVELS, SECURITY_USER, GUARDING_TYPE_RETAIL } from 'core/utils/value';
import { stripHtmlTags } from 'core/utils/helpers';
import { fetchSiteDetail } from 'core/sites/sitesApi';
import { clearSiteDetail } from 'core/sites/sitesSlice';
import { createVisitRequest } from 'core/visitRequestForm/visitRequestFormApi';
import { createAccessRequest } from 'core/accessRequestForm/accessRequestFormApi';
import { createGuardingRequest } from 'core/guardingRequestForm/guardingRequestFormApi';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import AccessTypeModal from './components/AccessTypeModal';
import BookAccessButton from './components/BookAccessButton';
import OutOfBoundsModal from './components/OutOfBoundsModal';
import RetailSiteInfo from './components/RetailSiteInfo';
import OpenHoursList from './components/OpenHoursList';
import SiteImages from './components/SiteImages';
import AlarmCodesModal from './components/AlarmCodesModal';
import DoorCctvCodesModal from './components/DoorCctvCodesModal';
import TwoManSiteNotesModal from './components/TwoManSiteNotesModal';
import './siteInfo.scss';

/**
 * Build the navigation state carried into a request form launched from this
 * site. `backTo` is the return path; `originIdx` records this page's position in
 * the history stack so the form's Back control can pop straight back here
 * instead of leaving form steps behind.
 */
const buildRequestFormState = (siteRouteId?: string): RequestFormNavigationState => {
    const currentIdx = (window.history.state as { idx?: number } | null)?.idx;
    return {
        // Guard against a missing route param so we never produce /site/undefined;
        // with no resolvable origin the form falls back to the dashboard.
        backTo: siteRouteId ? `/site/${siteRouteId}` : '/dashboard',
        ...(typeof currentIdx === 'number' ? { originIdx: currentIdx } : {}),
    };
};

const SiteInfo: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { siteDetail, isFetchingSiteDetail, siteDetailError } = useSelector(
        (state: CombinedStates) => state.sites,
    );

    const [accessTypeModalVisible, setAccessTypeModalVisible] = useState(false);
    const [outOfBoundsModalVisible, setOutOfBoundsModalVisible] = useState(false);
    const [twoManNotesModalVisible, setTwoManNotesModalVisible] = useState(false);
    const [alarmCodesModalVisible, setAlarmCodesModalVisible] = useState(false);
    const [doorCctvCodesModalVisible, setDoorCctvCodesModalVisible] = useState(false);
    const [isBooking, setIsBooking] = useState(false);
    // isBooking drives the button's disabled/spinner state, but a state value
    // read inside the handler is stale until React re-renders, so a fast
    // double click got through twice. The ref blocks the second click at once.
    const bookingInFlight = useRef(false);
    const [incidentsDropdownOpen, setIncidentsDropdownOpen] = useState(false);

    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    /**
     * Fetch site detail when component mounts or route id changes.
     *
     * Dependency array intentionally limited to [id]:
     * - dispatch is stable (from useDispatch) and doesn't need to be a dependency
     * - We only want to refetch when the route parameter changes, not on every render
     * - Including dispatch would not change behavior but adds unnecessary noise
     */
    useEffect(() => {
        if (id) {
            dispatch(fetchSiteDetail(id));
        }

        return () => {
            dispatch(clearSiteDetail());
        };
    }, [id, dispatch]);

    useEffect(() => {
        if (siteDetail?.isOutOfBounds) {
            setOutOfBoundsModalVisible(true);
        }
    }, [siteDetail]);

    const handleBookVisitorRequest = useCallback(async () => {
        if (!siteDetail || isBooking || bookingInFlight.current) return;
        bookingInFlight.current = true;

        setIsBooking(true);
        try {
            const resultAction = await dispatch(createVisitRequest({ siteId: siteDetail.id }));
            if (createVisitRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    navigate(`/visitor-request/${payload.visitRequest.encryptedId}/step/2`, { state: buildRequestFormState(id) });
                }
            } else if (createVisitRequest.rejected.match(resultAction)) {
                message.error('Failed to create visit request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        } finally {
            bookingInFlight.current = false;
            setIsBooking(false);
        }
    }, [dispatch, navigate, siteDetail, isBooking, id]);

    const handleBookGeneralAccess = useCallback(async () => {
        if (!siteDetail || isBooking || bookingInFlight.current) return;

        // A retail store holds no site areas or access types, and its booking
        // chain skips the Access Type step, so this rule would make every store
        // unbookable from here. The API exempts retail the same way, which is
        // why the Sites list could book a store that this screen refused.
        const isRetailSite = siteDetail.siteType?.guardingType === 'retail';

        if (!isRetailSite && siteDetail.hasAccessTypes === false) {
            message.error('No access types have been configured for this site. Please contact an administrator.');
            return;
        }

        // Taken only once the checks above have passed: an early return after
        // this point would skip the finally below and leave every booking
        // button on the page silently ignoring clicks.
        bookingInFlight.current = true;
        setIsBooking(true);
        try {
            const resultAction = await dispatch(createAccessRequest({ siteId: siteDetail.id }));
            if (createAccessRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    // Retail general access starts on Date (step 2); Job Details moves after Visitors.
                    const firstStep = payload.stepData?.isRetail ? 2 : 1;
                    navigate(`/access-request/${payload.visitRequest.encryptedId}/step/${firstStep}`, { state: buildRequestFormState(id) });
                }
            } else if (createAccessRequest.rejected.match(resultAction)) {
                message.error('Failed to create access request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        } finally {
            bookingInFlight.current = false;
            setIsBooking(false);
        }
    }, [dispatch, navigate, siteDetail, isBooking, id]);

    const handleBookGuarding = useCallback(async () => {
        if (!siteDetail || isBooking || bookingInFlight.current) return;
        bookingInFlight.current = true;

        setIsBooking(true);
        try {
            const resultAction = await dispatch(createGuardingRequest({ siteId: siteDetail.id }));
            if (createGuardingRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    // Retail guarding starts on Date (step 2); Job Details moves after Visitors.
                    const firstStep = payload.stepData?.isRetail ? 2 : 1;
                    navigate(`/guarding-request/${payload.visitRequest.encryptedId}/step/${firstStep}`, { state: buildRequestFormState(id) });
                }
            } else if (createGuardingRequest.rejected.match(resultAction)) {
                message.error('Failed to create guarding request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        } finally {
            bookingInFlight.current = false;
            setIsBooking(false);
        }
    }, [dispatch, navigate, siteDetail, isBooking, id]);

    const isRetailSite = siteDetail?.siteType?.guardingType === GUARDING_TYPE_RETAIL;
    const isVisitorRequest = siteDetail?.siteType?.isVisitorRequest ?? false;
    const isAdmin = ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel);
    const isSecurity = userData?.role?.accessLevel === SECURITY_USER;
    // The operational site record (response code, air horn, MFS region, Maximo
    // ref, LEB info, keys, site notes) is for the people attending the site, so
    // a basic user booking a visit doesn't see it. The API withholds these
    // fields for the same roles — this only keeps the grid from rendering a
    // column of dashes. Retail pages have their own component and are unchanged.
    const canSeeOperationalInfo = isAdmin || isSecurity;

    const renderContent = () => {
        if (isFetchingSiteDetail) {
            return (
                <div className="loading-container">
                    <Spin size="large" />
                </div>
            );
        }

        if (siteDetailError || !siteDetail) {
            return (
                <div className="error-container" role="alert">
                    <h2 className="error-title">Unable to Load Site</h2>
                    <p className="error-message">
                        {siteDetailError || 'Site not found.'}
                    </p>
                    <button
                        type="button"
                        className="back-button"
                        onClick={() => navigate('/sites')}>
                        Back to Sites
                    </button>
                </div>
            );
        }

        const { siteInfo } = siteDetail;

        return (
            <>
                <div className="site-header" data-tour="site-info-header">
                    <button
                        type="button"
                        className="back-arrow"
                        onClick={() => navigate(-1)}
                        aria-label="Go back">
                        <LeftOutlined />
                    </button>
                    <div className="site-title-section">
                        <h1 className="page-title">{siteDetail.name}</h1>
                        <p className="page-subtitle">{siteDetail.fullAddress}</p>
                    </div>
                    {!isAdmin && (
                        <div className="header-actions">
                            {isSecurity && isRetailSite && (
                                <button
                                    type="button"
                                    className="action-btn"
                                    onClick={() => setDoorCctvCodesModalVisible(true)}>
                                    Door & CCTV Codes
                                </button>
                            )}
                            <BookAccessButton
                                siteId={siteDetail.id}
                                isVisitorRequest={isVisitorRequest}
                                isLoading={isBooking}
                                accessBookingAvailable={siteDetail.accessBookingAvailable ?? true}
                                onBookVisitorRequest={handleBookVisitorRequest}
                                onBookGeneralAccess={handleBookGeneralAccess}
                                onBookGuarding={handleBookGuarding} />
                        </div>
                    )}
                </div>

                {isAdmin && (
                    <div className="admin-actions-row">
                        <div className="admin-actions-left">
                            <div className="book-access-button-container">
                                <Dropdown
                                    trigger={['click']}
                                    placement="bottomLeft"
                                    align={{ offset: [0, 4] }}
                                    overlayClassName="book-access-dropdown"
                                    getPopupContainer={(trigger) => trigger.parentElement || document.body}
                                    onOpenChange={setIncidentsDropdownOpen}
                                    menu={{
                                        items: [
                                            {
                                                key: 'view-incidents',
                                                label: 'View Incidents',
                                                onClick: () => navigate(`/site/${siteDetail.encryptedId}/incidents`, {
                                                    state: { siteName: siteDetail.name, siteId: String(siteDetail.id) },
                                                }),
                                            },
                                            {
                                                key: 'create-incident',
                                                label: 'Create Incident',
                                                onClick: () => navigate(`/site/${siteDetail.encryptedId}/incidents`, {
                                                    state: { siteName: siteDetail.name, siteId: String(siteDetail.id), openReport: true },
                                                }),
                                            },
                                        ],
                                    }}>
                                    <button
                                        type="button"
                                        className="book-button"
                                        aria-haspopup="menu"
                                        aria-expanded={incidentsDropdownOpen}>
                                        Incidents
                                        <Icon
                                            component={(incidentsDropdownOpen ? TriangleUpIcon : TriangleDownIcon) as React.ForwardRefExoticComponent<unknown>}
                                            aria-hidden="true" />
                                    </button>
                                </Dropdown>
                            </div>
                            <button
                                type="button"
                                className="action-btn"
                                onClick={() => {
                                    if (!isRetailSite) {
                                        setAlarmCodesModalVisible(true);
                                    } else {
                                        setDoorCctvCodesModalVisible(true);
                                    }
                                }}>
                                {isRetailSite ? 'Door & CCTV Codes' : 'Alarm Codes'}
                            </button>
                        </div>
                        <div className="admin-actions-right">
                            <BookAccessButton
                                siteId={siteDetail.id}
                                isVisitorRequest={isVisitorRequest}
                                isLoading={isBooking}
                                accessBookingAvailable={siteDetail.accessBookingAvailable ?? true}
                                onBookVisitorRequest={handleBookVisitorRequest}
                                onBookGeneralAccess={handleBookGeneralAccess}
                                onBookGuarding={handleBookGuarding} />
                        </div>
                    </div>
                )}

                {isRetailSite && siteDetail ? <RetailSiteInfo siteDetail={siteDetail} isAdmin={isAdmin} /> : (
                    <div className="site-info-card">
                        {/* Row 1: Site codes and references */}
                        <div className="info-grid-section">
                            <div className="info-grid">
                                <div className="info-item">
                                    <div className="item-label">Mitie Code (Static):</div>
                                    <div className="item-value">{siteDetail?.staticSiteCode || '-'}</div>
                                </div>
                                {canSeeOperationalInfo && (
                                    <div className="info-item">
                                        <div className="item-label">Mitie Code (Response):</div>
                                        <div className="item-value">{siteInfo?.mitieCodeResponse || '-'}</div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <div className="item-label">Site Code:</div>
                                    <div className="item-value">{siteInfo?.moriCode || '-'}</div>
                                </div>
                                {canSeeOperationalInfo && (
                                    <>
                                        <div className="info-item">
                                            <div className="item-label">Air Horn Ext:</div>
                                            <div className="item-value">{siteInfo?.airHornExt || '-'}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="item-label">MFS Region:</div>
                                            <div className="item-value">{siteInfo?.mfsIpDomain || '-'}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="item-label">Maximo Ref:</div>
                                            <div className="item-value">{siteInfo?.maximoRef || '-'}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="item-label">LEB Info:</div>
                                            <div className="item-value">{siteInfo?.lebInfo || '-'}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Row 2: Site details */}
                        <div className="info-grid-section">
                            <div className="info-grid">
                                {isAdmin && (
                                    <div className="info-item">
                                        <div className="item-label">Concept Region:</div>
                                        <div className="item-value">{siteInfo?.conceptReference || '-'}</div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <div className="item-label">2 Person Site:</div>
                                    <div className="item-value">{siteInfo?.twoManSite ? 'Yes' : 'No'}</div>
                                </div>
                                <div className="info-item">
                                    <div className="item-label">Keys Required:</div>
                                    <div className="item-value">{siteInfo?.keysRequired || '-'}</div>
                                </div>
                                {isAdmin && (
                                    <div className="info-item info-item--full">
                                        <div className="item-label">Gate Codes:</div>
                                        <div className="item-value" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                            {siteDetail?.gateCodes?.length
                                                ? siteDetail.gateCodes.map((gc) => (
                                                    <div key={gc.id}>{gc.name ?? 'Unnamed'}: {gc.code ?? '-'}</div>
                                                ))
                                                : '-'}
                                        </div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <div className="item-label">Access Type:</div>
                                    <div className="item-value">
                                        {siteDetail.accessType?.name || '-'}
                                        {siteDetail.accessType && (
                                            <button
                                                type="button"
                                                className="access-type-eye"
                                                onClick={() => setAccessTypeModalVisible(true)}
                                                aria-label="View access type details">
                                                <EyeOutlined />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="info-item">
                                    <div className="item-label">What3Words:</div>
                                    <div className="item-value">
                                        {siteInfo?.what3Words ? (
                                            <a
                                                href={`https://what3words.com/${siteInfo.what3Words}`}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {'///'}{siteInfo.what3Words}
                                            </a>
                                        ) : '-'}
                                    </div>
                                </div>
                                {isAdmin && (
                                    <div className="info-item">
                                        <div className="item-label">Incident Risk Level:</div>
                                        <div className="item-value">{siteInfo?.incidentRiskLevelLabel || '-'}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* A visitor-request site is booked around the days it
                            is open, the same way a retail store is booked
                            around its opening times. */}
                        {isVisitorRequest && (
                            <div className="info-grid-section">
                                <div className="info-item">
                                    <div className="item-label">Open Days:</div>
                                    <OpenHoursList openDays={siteDetail.openDays ?? []} />
                                </div>
                            </div>
                        )}

                        {/* Notes and Directions - sanitized to strip any HTML */}
                        {((canSeeOperationalInfo && siteInfo?.siteNotes)
                            || siteInfo?.directions
                            || (siteInfo?.twoManSite && siteInfo?.twoManSiteNotes)) && (
                            <div className="notes-section">
                                {canSeeOperationalInfo && siteInfo?.siteNotes && (
                                    <div className="notes-block">
                                        <div className="notes-label">Site Notes:</div>
                                        <div className="notes-content">{stripHtmlTags(siteInfo.siteNotes)}</div>
                                    </div>
                                )}
                                {siteInfo?.directions && (
                                    <div className="notes-block">
                                        <div className="notes-label">Directions:</div>
                                        <div className="notes-content">{stripHtmlTags(siteInfo.directions)}</div>
                                    </div>
                                )}
                                {siteInfo?.twoManSite && (
                                    <div className="notes-block">
                                        <div className="notes-label">
                                            Two Person Site Notes:
                                            {isAdmin && (
                                                <button
                                                    type="button"
                                                    className="edit-notes-btn"
                                                    onClick={() => setTwoManNotesModalVisible(true)}
                                                    aria-label="Edit two person site notes">
                                                    <EditIcon />
                                                </button>
                                            )}
                                        </div>
                                        <div className="notes-content">
                                            {siteInfo.twoManSiteNotes ? stripHtmlTags(siteInfo.twoManSiteNotes) : '-'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {isAdmin && !isRetailSite && (
                    <SiteImages
                        encryptedId={siteDetail.encryptedId}
                        siteImages={siteDetail.siteImages || []} />
                )}

                <AccessTypeModal
                    visible={accessTypeModalVisible}
                    onClose={() => setAccessTypeModalVisible(false)}
                    isAdmin={isAdmin}
                    accessType={siteDetail.accessType} />

                <OutOfBoundsModal
                    visible={outOfBoundsModalVisible}
                    message={siteDetail?.outOfBoundsMessage}
                    onClose={() => setOutOfBoundsModalVisible(false)} />

                <TwoManSiteNotesModal
                    visible={twoManNotesModalVisible}
                    onClose={() => setTwoManNotesModalVisible(false)}
                    encryptedId={siteDetail.encryptedId}
                    initialNotes={siteInfo?.twoManSiteNotes || null} />

                {!isRetailSite && (
                    <AlarmCodesModal
                        visible={alarmCodesModalVisible}
                        onClose={() => setAlarmCodesModalVisible(false)}
                        encryptedId={siteDetail.encryptedId} />
                )}

                {isRetailSite && (isAdmin || isSecurity) && (
                    <DoorCctvCodesModal
                        visible={doorCctvCodesModalVisible}
                        onClose={() => setDoorCctvCodesModalVisible(false)}
                        encryptedId={siteDetail.encryptedId} />
                )}

            </>
        );
    };

    return (
        <Main
            className="site-info"
            title={<div>Site Info</div>}
            hideSubHeader
            subHeaderTitle="Site Info"
            subHeaderHasBackLink
            subHeaderBackLink="/sites"
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            breadcrumb={[]}>
            <div className="main-container site-info-page">
                {renderContent()}
            </div>
        </Main>
    );
};

export default SiteInfo;
